<template>
  <ul
    class="grid-x grid-spacing tiles"
    :class="displayMode"
  >
    <template
      v-for="tile in options"
      :key="tile.id"
    >
      <li
        class="cell"
        :class="cellSpacing"
      >
        <button
          :id="tile.id"
          :name="name"
          class="selection-tile"
          tabindex="0"
          role="button"
          :aria-pressed="isSelected(tile)"
          :aria-labelledby="`${($slots.default ? 'slot' : 'span')}${contentId}_${tile.id}`"
          :class="{
            selected: isSelected(tile),
            unavailable: tile.unavailable,
            'size-tile': sizeTile,
            'small-height': smallHeight,
            'xs-tile': isXs,
          }"
          type="button"
          @click="onClick(tile)"
        >
          <div
            v-if="$slots.default"
            :id="`slot${contentId}_${tile.id}`"
          >
            <slot :tile="tile" />
          </div>
          <template v-if="!$slots.default">
            <span :id="`span${contentId}_${tile.id}`">{{ tile.text }}</span>
            <span
              v-if="tile.subText"
              class="subtext"
            >{{ tile.subText }}</span>
          </template>
        </button>
      </li>
    </template>
  </ul>
</template>
<script>
import { watch } from 'vue';
import { veevalidate } from '@vue-util/forms';
import generateRandomId from '../../utils/randomNumber';

const { useField } = veevalidate;
export default {
  name: 'SelectionTile',
  props: {
    name: {
      type: String,
      default: 'tile',
    },
    options: {
      type: Array,
      default: () => [],
    },
    required: {
      type: Boolean,
      default: false,
    },
    cellSpacing: {
      type: String,
      default: 'small-2',
    },
    selectionMode: {
      type: String,
      default: 'single',
    },
    modelValue: {
      type: [Array, String, Number],
      default: null,
    },
    sizeTile: {
      type: Boolean,
      default: false,
    },
    smallHeight: {
      type: Boolean,
      default: false,
    },
    tileVariant: {
      type: String,
      default: 'normal',
    },
    displayMode: {
      type: String,
      default: 'scroll',
      /**
       * @param {string} value
       */
      validator(value) {
        if (!value) {
          return false;
        }
        return ['scroll', 'stack'].includes(value);
      },
    },
  },
  emits: ['update:modelValue'],
  computed: {
    isXs() {
      return this.tileVariant === 'xs';
    },
  },
  setup(props, { emit }) {
    const contentId = generateRandomId();
    const { value: selected, validate } = useField(
      props.name,
      props.required
        ? (item) => {
          if (typeof item === 'object' && item && item.length > 0) {
            return true;
          } if (
            (typeof item === 'string' || typeof item === 'number')
              && item
          ) {
            return true;
          }
          if (typeof item === 'object') {
            return 'Please choose at least one option.';
          }
          return 'Please choose one option.';
        }
        : undefined,
      {
        initialValue: props.modelValue,
      },
    );
    function isSelected(tile) {
      return props.selectionMode === 'single'
        ? tile.id === selected.value
        : selected.value.includes(tile.id);
    }
    async function tileValidation() {
      await validate();
    }
    function singleSelection(tile) {
      const toUpdate = selected.value !== tile.id;
      if (toUpdate) {
        selected.value = tile.id;
      }
      return toUpdate;
    }
    function multipleSelection(tile) {
      if (selected.value.includes(tile.id)) {
        selected.value.splice(selected.value.indexOf(tile.id), 1);
      } else if (!selected.value.includes(tile.id)) {
        selected.value.push(tile.id);
      }
    }
    function onClick(tile) {
      if (props.selectionMode === 'single' && !singleSelection(tile)) {
        return;
      } if (props.selectionMode === 'multiple') {
        multipleSelection(tile);
      }
      emit('update:modelValue', selected.value);
      tileValidation();
    }

    watch(() => props.modelValue, (newVal, oldVal) => {
      if (Array.isArray(newVal)) {
        selected.value = props.modelValue;
      } else if (newVal !== oldVal) {
        selected.value = props.modelValue;
      }
      tileValidation();
    });
    return {
      selected,
      isSelected,
      onClick,
      contentId,
    };
  },
};
</script>
<style lang="scss" scoped>
$grid-margin-gutters: rem-calc(16);
@import "~@core/foundation/scss/foundation-6.7.4/_vendor/sassy-lists/stylesheets/helpers/true";
@import "~@core/foundation/scss/foundation-6.7.4/_vendor/sassy-lists/stylesheets/functions/purge";
@import "~@core/foundation/scss/foundation-6.7.4/_vendor/sassy-lists/stylesheets/functions/remove";
@import "~@core/foundation/scss/foundation-6.7.4/_vendor/sassy-lists/stylesheets/functions/replace";
@import "~@core/foundation/scss/foundation-6.7.4/_vendor/sassy-lists/stylesheets/functions/to-list";
@import '~@core/foundation/scss/foundation-6.7.4/scss/xy-grid/_gutters.scss';
@import '~@core/foundation/scss/foundation-6.7.4/scss/xy-grid/_cell.scss';
@import '~@core/foundation/scss/foundation-6.7.4/scss/xy-grid/_classes.scss';
@import "~@core/csg/src/scss/tiles/tiles.scss";
@import "~@core/csg/src/scss/forms/forms.scss";
@include csg-tiles;
@include csg-forms;

.tiles {
  list-style-type: none;
  margin: 0;
  padding: 0;
}

.grid-spacing {
  margin: rem-calc(-8 0);
  @include xy-gutters($negative: true, $gutter-position: right left);
  > .cell {
    margin: rem-calc(8 0);
    @include xy-cell($output: (size gutters));
  }
  @include -zf-each-breakpoint() {
    @include -xy-breakpoint-cell-classes($-zf-size, $-zf-size, false);
  }
}

.grid-x.tiles.scroll {
  @include breakpoint(small down) {
    flex-wrap: nowrap;
    overflow: scroll;
  }
}
</style>
